import React, { useEffect, useState } from "react"
import { Col, Row, UncontrolledAlert, Spinner } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField } from "availity-reactstrap-validation"
import { Link, useParams } from "react-router-dom"

const EditRoute = () => {
  const { id } = useParams()
  const [route, setRoute] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pageLoad, setPageLoad] = useState(true)
  const [companies, setCompanies] = useState([])
  const [plants, setPlants] = useState([])
  const [centers, setCenters] = useState([])
  const [selectedCompany, setSelectedCompany] = useState("")
  const [selectedPlant, setSelectedPlant] = useState("")
  const [loadingCenters, setLoadingCenters] = useState(false)

  useEffect(() => {
    const loadData = async () => {
      try {
        const [rResp, compResp, plantResp] = await Promise.all([
          api.get("route/" + id),
          api.get("companies"),
          api.get("plants")
        ])
        const rData = rResp.data.data?.[0] || rResp.data.data
        setRoute(rData)
        const compData = Array.isArray(compResp.data.data) ? compResp.data.data : compResp.data.data?.data || []
        setCompanies(compData)
        const plantData = Array.isArray(plantResp.data.data) ? plantResp.data.data : plantResp.data.data?.data || []
        setPlants(plantData)

        // Set company from route data
        if (rData.company) setSelectedCompany(String(rData.company))
        else if (compData.length === 1) setSelectedCompany(String(compData[0].id))

        // Derive plant from the chilling center that this route belongs to
        if (rData.chillingCenter) {
          try {
            const ccResp = await api.get("center/" + rData.chillingCenter)
            const ccData = ccResp.data.data?.[0] || ccResp.data.data
            if (ccData && ccData.plant_id) {
              setSelectedPlant(String(ccData.plant_id))
              // Load centers for that plant
              const centersResp = await api.get("chillings-by-plant/" + ccData.plant_id)
              setCenters(Array.isArray(centersResp.data.data) ? centersResp.data.data : centersResp.data.data?.data || [])
            }
          } catch (e) { console.error(e) }
        }
      } catch (e) { console.error(e) }
      setPageLoad(false)
    }
    loadData()
  }, [id])

  // Reload centers when plant changes (user interaction)
  const handlePlantChange = async (plantId) => {
    setSelectedPlant(plantId)
    setCenters([])
    if (!plantId) return
    setLoadingCenters(true)
    try {
      const resp = await api.get("chillings-by-plant/" + plantId)
      setCenters(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || [])
    } catch (e) { console.error(e) }
    setLoadingCenters(false)
  }

  const handleValidSubmit = async (event, values) => {
    setLoading(true)
    try {
      // Build only the data the API needs
      const putData = {
        company: hasMultiCompanies ? values.company : String(companies[0].id),
        chillingCenter: values.chillingCenter,
        routeName: values.routeName,
        routeNumber: values.routeNumber,
      }
      const resp = await api.put("update-route/" + id, putData)
      if (resp.data.success) { setSubmitted(true); setTimeout(() => setSubmitted(false), 4000) }
    } catch (e) {
      console.error(e)
      alert(e.response?.data?.message || e.response?.data?.error || "Failed to update route")
    }
    setLoading(false)
  }

  if (pageLoad) return <div className="dashBody"><div className="text-center py-5"><Spinner color="primary" style={{ width: "3rem", height: "3rem" }} /><p className="mt-3 text-muted">Loading Route...</p></div></div>
  if (!route) return <div className="dashBody"><p>Route not found</p></div>

  const hasMultiCompanies = companies.length > 1
  const filteredPlants = hasMultiCompanies && selectedCompany
    ? plants.filter(p => String(p.company_id) === selectedCompany)
    : plants

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4>Edit Route</h4></Col>
          <Col md={6} style={{ textAlign: "right" }}>
            <Link to="/routes" className="dashBtn"><i className="fas fa-chevron-left"></i> <b>Back</b></Link>
          </Col>
        </Row>
      </div>
      <div className="dashContent">
        <AvForm className="form-horizontal mt-4" onValidSubmit={handleValidSubmit}>

          <h6 style={{ color: "#6366f1", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px" }}>
            <i className="fas fa-route"></i> Route Details
          </h6>

          {hasMultiCompanies && (
            <Row>
              <Col md={4}><div className="mb-3">
                <AvField type="select" name="company" label="Company *" required value={selectedCompany}
                  onChange={e => { setSelectedCompany(e.target.value); setSelectedPlant(""); setCenters([]) }}>
                  <option value="">Select Company</option>
                  {companies.map((c, i) => <option key={i} value={String(c.id)}>{c.name}</option>)}
                </AvField>
              </div></Col>
            </Row>
          )}

          <Row>
            <Col md={3}><div className="mb-3">
              <AvField type="select" name="plant_filter" label="Plant *" value={selectedPlant}
                onChange={e => handlePlantChange(e.target.value)}>
                <option value="">Choose a plant . . .</option>
                {filteredPlants.map((p, i) => <option key={i} value={String(p.id)}>{p.name}</option>)}
              </AvField>
            </div></Col>
            <Col md={3}><div className="mb-3">
              <AvField type="select" name="chillingCenter" label={<>Chilling Center * {loadingCenters && <Spinner size="sm" color="primary" className="ms-1" />}</>} required value={String(route.chillingCenter || "")}>
                <option value="">{loadingCenters ? "Loading..." : centers.length ? "Choose a chilling center . . ." : "Select plant first"}</option>
                {centers.map((c, i) => <option key={i} value={String(c.id)}>{c.name}{c.number ? ` (${c.number})` : ""}</option>)}
              </AvField>
            </div></Col>
            <Col md={3}><div className="mb-3">
              <AvField name="routeName" label="Route Name *" value={route.routeName} type="text" required />
            </div></Col>
            <Col md={3}><div className="mb-3">
              <AvField name="routeNumber" label="Route Number *" value={route.routeNumber} type="text" required />
            </div></Col>
          </Row>

          <div className="mb-3 row mt-4">
            <div className="col-6">
              {submitted && <UncontrolledAlert color="success"><strong>Route Updated!</strong></UncontrolledAlert>}
            </div>
            <div className="col-6 text-end">
              <button className="btn btn-primary w-md waves-effect waves-light" type="submit" disabled={loading}>
                {loading ? <><Spinner size="sm" /><span style={{ paddingLeft: "10px" }}>Updating...</span></> : <span><i className="fas fa-save"></i> Update Route</span>}
              </button>
            </div>
          </div>
        </AvForm>
      </div>
    </div>
  )
}

export default EditRoute
